/**
 * Maps a live connector document's `run` block onto the timeline UI's stage
 * data shape (see docs/timeline-ui.md). Pure + defensive — it's fed straight
 * from an onSnapshot, so every field is treated as possibly-missing.
 *
 * Two stages:
 *   1. Connect   — the sequential fetch/classify/discover/probe steps, with the
 *                  chosen tier surfaced as a trigger badge.
 *   2. Validate  — a parallel group of the five date probes, each listing the
 *                  tee-times it pulled (or a clean "no availability").
 */

const TIER_BADGE = {
	API: { icon: "zap", label: "Direct API", variant: "success" },
	BROWSER: { icon: "globe", label: "Headless browser", variant: "info" },
	AGENT: { icon: "activity", label: "AI browser agent", variant: "info" },
}

/** ISO (YYYY-MM-DD) → "Tue 12 Aug 2026". */
const prettyDate = (iso) => {
	if (!iso) return "Date"
	const [y, m, d] = iso.split("-").map(Number)
	if (!y || !m || !d) return iso
	const dt = new Date(Date.UTC(y, m - 1, d))
	return dt.toLocaleDateString("en-GB", {
		weekday: "short",
		day: "numeric",
		month: "short",
		year: "numeric",
		timeZone: "UTC",
	})
}

const teeLabel = (t) => {
	const bits = [t.time]
	if (t.slots) bits.push(`${t.slots} open`)
	return bits.join("  ·  ")
}

// The timeline has no dedicated "error" pip, so an errored step/probe resolves
// to a complete pip with the reason carried in the text/summary alongside it.
const pipStatus = (status) => (status === "error" ? "complete" : status || "pending")

const buildDateActivity = (probe, index) => {
	const times = Array.isArray(probe.teeTimes) ? probe.teeTimes : []
	const status = pipStatus(probe.status)
	const capped = times.slice(0, 8)

	let items
	let summary
	let metaLabel

	if (probe.status === "error") {
		items = [{ id: "err", status: "complete", content: probe.message || "Could not read this date" }]
		summary = `⚠ ${probe.message || "Error"}`
		metaLabel = "Failed"
	} else if (status === "complete" && !times.length) {
		items = [{ id: "none", status: "complete", content: probe.message || "No tee times available" }]
		summary = "No availability"
		metaLabel = "0 found"
	} else if (status === "complete") {
		items = capped.map((t, k) => ({ id: k, status: "complete", content: teeLabel(t) }))
		if (times.length > capped.length) items.push({ id: "more", status: "complete", content: `+ ${times.length - capped.length} more` })
		summary = `${times.length} tee-time${times.length === 1 ? "" : "s"}`
		metaLabel = `${times.length} found`
	} else {
		// pending / active
		items = [{ id: "wait", status, content: status === "active" ? "Reading availability…" : "Waiting" }]
		summary = null
	}

	return {
		id: probe.date || `date-${index}`,
		status,
		title: prettyDate(probe.date),
		meta: metaLabel ? { steps: metaLabel } : undefined,
		summary,
		items,
	}
}

export function buildConnectorStages(connector) {
	const run = connector?.run || {}
	const steps = Array.isArray(run.steps) ? run.steps : []
	const dates = Array.isArray(run.dates) ? run.dates : []
	const phase = run.phase
	const decision = run.decision
	const failed = phase === "failed"

	// ----- Connect stage -----
	const connectComplete = Boolean(decision) || ["probing", "validating", "done"].includes(phase)
	const connectStatus = connectComplete
		? "complete"
		: phase === "fetching" || phase === "classifying"
		? "active"
		: failed
		? "complete"
		: steps.length
		? "active"
		: "pending"

	const connectItems = steps.length
		? steps.map((s, i) => ({
				id: s.key || `step-${i}`,
				status: pipStatus(s.status),
				content: s.label,
				sub: s.detail,
		  }))
		: [{ id: "init", status: phase ? "active" : "pending", content: "Reaching the URL" }]

	const connectStage = {
		id: "connect",
		header: { icon: "link", title: connector?.label || connector?.url || "Connector" },
		trigger: decision ? TIER_BADGE[decision.tier] || { label: decision.tier } : undefined,
		activity: {
			status: connectStatus,
			title: "Establishing connection",
			summary: decision?.reason,
			items: connectItems,
		},
	}

	const stages = [connectStage]

	// ----- Validate stage -----
	const showValidate = dates.length > 0 || ["probing", "validating", "done"].includes(phase)
	if (showValidate) {
		const dateActivities = dates.length
			? dates.map(buildDateActivity)
			: Array.from({ length: 5 }, (_, i) => ({
					id: `pending-${i}`,
					status: "pending",
					title: "Random date",
					items: [{ id: "wait", status: "pending", content: "Waiting" }],
			  }))

		const groupStatus = !dates.length
			? "active"
			: dates.every((d) => d.status === "complete" || d.status === "error")
			? "complete"
			: "active"
		const validated = dates.length > 0 && dates.every((d) => d.status === "complete")

		const validateTrigger =
			groupStatus !== "complete"
				? { label: "Validating dates…", variant: "loading" }
				: validated
				? { icon: "check", label: "All 5 dates validated", variant: "success" }
				: { icon: "alert-triangle", label: "Some dates failed", variant: "default" }

		stages.push({
			id: "validate",
			header: { icon: "calendar", title: "Validate 5 random dates" },
			trigger: validateTrigger,
			group: {
				status: groupStatus,
				title: "Checking availability across 5 dates",
				activities: dateActivities,
			},
		})
	}

	return stages
}
